import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ExternalLink, Phone, MapPin, Snowflake, Sun, Leaf, Flower2,
  Fish, Mountain, Waves, Trees, Clock, Star, Anchor, Camera
} from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

// Four Seasons content
const seasons = [
  {
    name: 'Winter',
    icon: Snowflake,
    color: 'bg-blue-600',
    description: 'The Kawarthas and Haliburton Highlands make great fun of winter with crisp temperatures and a soft blanket of snow.',
    activities: [
      'Snowmobiling on 49,000 km of groomed trails',
      'Ice fishing on Jack Lake',
      'Alpine skiing at Devil\'s Elbow and Sir Sam\'s',
      'Cross-country skiing',
      'Ice skating on the canal',
      'Ice climbing at Eagle\'s Nest'
    ]
  },
  {
    name: 'Spring',
    icon: Flower2,
    color: 'bg-green-500',
    description: 'Springtime in the region is alive with maple syrup festivals, sugar bush tours, and the beginning of an active fishing season.',
    activities: [
      'Maple syrup festivals and sugar bush tours',
      'Early season fishing',
      'Hiking as trails reopen',
      'Bird watching during migration',
      'Photography of spring blooms'
    ]
  },
  {
    name: 'Summer',
    icon: Sun,
    color: 'bg-amber-500',
    description: 'Our summers are known for water activity on the Trent-Severn Waterway, summer theatre, and golfing on more than a dozen courses.',
    activities: [
      'Swimming, water skiing, and boating',
      'Fishing for bass, walleye, and muskie',
      'Golfing at local courses',
      'Summer theatre performances',
      'Canoeing and kayaking',
      'Jet ski and boat rentals'
    ]
  },
  {
    name: 'Fall',
    icon: Leaf,
    color: 'bg-orange-600',
    description: 'The area boasts fairs and festivals in autumn enhanced by the enchanting colours of the season.',
    activities: [
      'Fall colour tours',
      'Harvest festivals and fairs',
      'Hiking among autumn foliage',
      'Apple picking',
      'Studio tours and art shows'
    ]
  }
];

// Main attractions from the old site
const attractions = [
  {
    name: 'Petroglyphs Provincial Park',
    description: 'Deep within a forest northeast of Peterborough is the largest known concentration of Aboriginal rock carvings in Canada. The centuries-old rock carvings give voice to the culture of the native Anishnabe. Carved into white marble rock face hundreds of years ago, the 900 petroglyphs depict turtles, snakes, birds, humans and other sacred images.',
    details: 'Hiking trails meander through surrounding forests, wetlands and rocky ridges. Explore The Learning Place interpretive centre. "The Rocks that Teach" are still revered by many as a sacred site.',
    location: '21 km southeast of Apsley at the end of Northey\'s Bay Road',
    phone: '(705) 877-2552',
    website: 'https://www.ontarioparks.com/park/petroglyphs',
    image: '/api/uploads/things_to_do/petroglyphs.jpg',
    category: 'Nature & Culture'
  },
  {
    name: 'Canadian Canoe Museum',
    description: 'North America\'s only canoe museum with 580 canoes and kayaks and 1,000 related artifacts. Experience a dramatic waterfall upon entering or hear creation stories inside a traditional Mi\'kmaq wigwam.',
    details: 'Feel what it was like living as a voyageur during the Fur Trade era, and enjoy the cottaging lifestyles of the early 20th century. The canoe is the ultimate link to Canada\'s rich cultural heritage.',
    location: '910 Monaghan Road, Peterborough',
    phone: '(705) 748-9153',
    website: 'https://www.canoemuseum.ca',
    image: '/api/uploads/things_to_do/canoe_museum.jpg',
    category: 'Museums'
  },
  {
    name: 'Lang Pioneer Village',
    description: 'Twenty restored 19th-century buildings, heritage gardens, farmyard animals and beautiful green spaces make up this living history museum that transports you back to pioneer Ontario.',
    details: 'Open from Victoria Day Weekend to Labour Day Weekend. Sunday to Friday 12-5pm, Saturdays and Holidays 1-4pm.',
    location: '10 km east of Peterborough on Highway 7, South on County Road 34',
    phone: '(705) 295-6694',
    website: 'https://www.langpioneervillage.ca',
    image: '/api/uploads/things_to_do/lang_pioneer.jpg',
    category: 'Heritage'
  },
  {
    name: 'Peterborough Lift Lock',
    description: 'Lock 21 on the Trent Severn Waterway is the highest hydraulic lift lock in the world. No external power is needed - the lift lock functions by gravity alone using the counterweight principle.',
    details: 'Built in 1904, it was named an Historic Mechanical Engineering Landmark by the American Society of Mechanical Engineers. Many locals skate on the canal below the lift lock in winter.',
    location: '300 Hunter Street East, Peterborough',
    phone: '(705) 750-4950',
    website: 'https://www.pc.gc.ca/en/lhn-nhs/on/trentsevern',
    image: '/api/uploads/things_to_do/lift_lock.jpg',
    category: 'Engineering Marvel'
  },
  {
    name: 'Warsaw Caves Conservation Area',
    description: 'A wide range of outdoor and adventure opportunities including hiking, spelunking (cave exploration), bird & nature observation, fishing, canoeing, and swimming.',
    details: 'The Conservation Area has 20 km of trails through a truly unique landscape. There are caves to explore including the famous ice cave where ice doesn\'t melt even in July! Sturdy footwear and a flashlight are essential.',
    location: '29 Caves Road, Warsaw',
    phone: '(705) 652-3161',
    website: 'https://www.warsawcaves.com',
    image: '/api/uploads/things_to_do/warsaw_caves.jpg',
    category: 'Adventure'
  },
  {
    name: 'Stoney Lake Cruises',
    description: 'Cruise aboard the Chippewa II, the largest cruise boat in the Kawarthas. This 133-passenger steamboat replica takes you on a 15 km cruise through 1,128 charted islands of Stony and Clear Lakes.',
    details: 'Live commentary covers St. Peter\'s Church on the Rock (accessible only by boat), the 22-acre Ronnie Hawkins estate, and the fascinating history of Stony Lake. Scenic cruises daily except Mondays. Evening Dinner & Dance Cruises Thursday & Saturday at 6pm.',
    location: '7 km north of Burleigh Falls off Hwy 28 on Mount Julian/Viamede Road',
    phone: '(705) 654-5253',
    website: 'https://www.stonylakecruises.com',
    image: '/api/uploads/things_to_do/chippewa_cruise.jpg',
    category: 'Water Tours'
  },
  {
    name: 'Whetung Ojibwa Centre',
    description: 'Experience the traditions of the past reflected in the present. A spectacular collection of Indigenous crafts, fine art, sculpture, masks, leather work, clothing, jewelry, music, and books from across Canada.',
    details: 'Located in the heart of Curve Lake Indian Reserve. Open year-round with free parking, wheelchair access and picnic area. Summer Tea Room serves traditional Indigenous food and "pow wow" fare.',
    location: 'Curve Lake Indian Reserve',
    phone: '(705) 657-3661',
    website: 'https://www.whetung.com',
    image: '/api/uploads/things_to_do/whetung.jpg',
    category: 'Indigenous Culture'
  },
  {
    name: 'Buckhorn Observatory',
    description: 'View the night sky through professional telescopes and binoculars. Committed to helping people understand the importance of preserving the night sky through outreach programs.',
    details: 'Open April to October with special viewing sessions for groups. Area resorts can arrange special onsite tours of the Moon and planets for their guests.',
    location: '2254 County Road 507, Buckhorn',
    phone: '(705) 657-2544',
    website: 'https://www.buckhornobservatory.com',
    image: '/api/uploads/things_to_do/buckhorn_observatory.jpg',
    category: 'Stargazing'
  },
  {
    name: 'Indian River Reptile Zoo',
    description: 'One of the most modern and exciting specialized animal centres in the world! See snakes, lizards, monitors, alligators, turtles, crocodiles, and special guest animals like monkeys.',
    details: 'State of the art, climate controlled facilities specifically designed to house reptiles. Open May - October, 10-5 daily.',
    location: '16 km east of Peterborough on HWY #7',
    phone: '(705) 639-1443',
    website: 'https://www.reptilezoo.org',
    image: '/api/uploads/things_to_do/reptile_zoo.jpg',
    category: 'Wildlife'
  },
  {
    name: 'Pine Point Diving - Jack Lake Scuba',
    description: 'Explore the underwater world of Jack Lake with certified PADI Divemasters Christina and Andre Forget. Clear water with hard rock/sand bottom and excellent visibility.',
    details: 'Great dive sites within a quick boat ride, excellent large dive boat. Facilities include bathroom, parking, equipment rinsing and drying. Certified divers only.',
    location: 'Jack Lake',
    phone: '(705) 740-0079',
    image: '/api/uploads/things_to_do/pine_point_diving.jpg',
    category: 'Scuba Diving'
  },
  {
    name: 'Lockside Trading Company',
    description: 'A charming 1850s home featuring over 5,000 square feet of unique home décor, gifts, and antiques. The casual, unhurried atmosphere invites leisurely browsing.',
    details: 'Originally built in the 1850s, Lockside is the oldest home in Young\'s Point and makes the perfect setting for an afternoon of treasure hunting.',
    location: 'Young\'s Point (20 min from Peterborough)',
    phone: '(705) 652-3940',
    website: 'https://www.lockside.com',
    image: '/api/uploads/things_to_do/lockside.jpg',
    category: 'Shopping'
  },
  {
    name: 'Peterborough Speedway',
    description: 'Canada\'s fastest 1/3 mile high speed paved oval, situated in the heart of cottage country. One of the busiest racing venues in Canada with approximately 32 race events per year.',
    details: 'Spectator facilities can accommodate over 6,000 people. Features one of the best lighting systems for televised night events.',
    location: '1710 Mt. Pleasant Rd., Peterborough',
    phone: '(705) 743-5880',
    website: 'https://www.peterboroughspeedway.com',
    image: '/api/uploads/things_to_do/speedway.jpg',
    category: 'Entertainment'
  }
];

// Golf courses
const golfCourses = [
  {
    name: 'Marvel Rapids Golf Course',
    holes: 9,
    description: 'A scenic nine-hole course within a 20-minute ride from Jack Lake.',
    address: '234 Jeff Rd, Apsley',
    phone: '(705) 656-4653',
    tollFree: '(800) 799-6103',
    website: 'http://www.marvelrapids.com',
    image: '/api/uploads/things_to_do/marvel_rapids_golf.jpg'
  },
  {
    name: 'Pine Crest Golf Club',
    holes: 18,
    description: 'Established in 1925, offers something for golfers of all skill levels.',
    address: '2455 Base Line, RR8, Peterborough',
    phone: '(705) 743-5953',
    website: 'http://www.pinecrestgolfclub.com',
    image: '/api/uploads/things_to_do/pine_crest_golf.jpg'
  },
  {
    name: 'Kawartha Golf & Country Club',
    holes: 18,
    description: 'Semi-private course opened in 1932 with all the hallmarks of Ontario golf.',
    address: '777 Clonsilla Ave, Peterborough',
    phone: '(705) 743-3737',
    website: 'http://www.kawarthagolf.ca',
    image: '/api/uploads/things_to_do/kawartha_golf.jpg'
  }
];

// Winter activities
const winterActivities = [
  {
    name: 'Snowmobiling Paradise',
    description: 'Ontario boasts 49,000 km of marked, interconnected and groomed trails - known as "snow highways." Explore rugged highland corridors, breathtaking lakeland countryside, and endless vistas of snow-covered trees.',
    image: '/api/uploads/things_to_do/snowmobiling.jpg',
    links: [{ name: 'Ontario Federation of Snowmobile Clubs', url: 'http://www.ofsc.on.ca' }]
  },
  {
    name: 'Paudash Trail Blazers Snowmobile Club',
    description: 'Established in 1976. The club maintains trails and operates "Saunders Inn" clubhouse where snowmobilers can rest and warm up.',
    image: '/api/uploads/things_to_do/paudash_sign.jpg',
    contact: { name: 'Kim Tucker', phone: '(705) 656-2292', tollFree: '1-800-385-8644' }
  },
  {
    name: 'Eagle\'s Nest Lookout',
    description: 'Panoramic view of the York River valley. Spectacular during fall colours, and in winter you can try ice climbing!',
    image: '/api/uploads/things_to_do/eagles_nest.jpg',
    location: 'Hastings Street N. (Hwy 62), Bancroft'
  }
];

// Useful links
const usefulLinks = [
  { name: 'County of Peterborough', url: 'http://www.county.peterborough.on.ca' },
  { name: 'Ministry of Natural Resources', url: 'http://www.mnr.gov.on.ca', description: 'Fishing regulations and outdoor info' },
  { name: 'Environment Canada Weather', url: 'http://weather.ec.gc.ca', description: 'Ontario weather forecasts' },
  { name: 'Canadian Coast Guard', url: 'http://www.ccg-gcc.gc.ca', description: 'Boating safety and regulations' },
  { name: 'Ontario Parks', url: 'http://www.ontarioparks.com', description: 'Provincial parks information' },
  { name: 'Hike Ontario', url: 'http://www.hikeontario.com', description: 'Hiking trail information' }
];

export default function ThingsToDoPage() {
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#1B4D3E] overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-[#D4AF37] text-sm tracking-widest uppercase mb-4">Land of Shining Waters</p>
            <h1 className="font-serif text-4xl md:text-5xl text-white mb-6">
              Things to See & Do
            </h1>
            <p className="text-white/80 text-lg leading-relaxed">
              <strong>"Kawartha"</strong> means "Land of Shining Waters" - the name First Nations people gave to this region 
              renowned for its sparkling lakes, rugged landscape and deep forests. From picnics to pubs, 
              swimming to sailing, angling to antiquing - it's all here in the Kawarthas!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white border-b border-[#E5E0DB]">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[#66605B] text-lg leading-relaxed text-center">
            The Kawarthas has been a destination of choice for vacationers and cottagers for well over a century. 
            Your stay at Big Bear Estate situates you in the heart of this renowned, exceptionally picturesque region. 
            Along the Otonabee River is the City of Peterborough - featuring first-class restaurants, unique shops, 
            a vibrant downtown, sports venues and many opportunities to enjoy live music, theatre, heritage and art.
          </p>
        </div>
      </section>

      {/* Four Seasons */}
      <section className="py-16 bg-[#F0EBE5]">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="font-serif text-3xl text-[#1C1C1C] mb-8 text-center">Four Seasons of Adventure</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {seasons.map((season, idx) => {
              const Icon = season.icon;
              return (
                <motion.div
                  key={season.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow"
                >
                  <div className={`w-12 h-12 ${season.color} rounded-full flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-serif text-xl text-[#1C1C1C] mb-3">{season.name}</h3>
                  <p className="text-[#66605B] text-sm mb-4">{season.description}</p>
                  <ul className="space-y-1">
                    {season.activities.slice(0, 4).map((activity, i) => (
                      <li key={i} className="text-xs text-[#66605B] flex items-start gap-2">
                        <span className="text-[#1B4D3E]">•</span>
                        {activity}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Attractions Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="font-serif text-3xl text-[#1C1C1C] mb-4 text-center">Local Attractions</h2>
          <p className="text-[#66605B] text-center mb-12 max-w-2xl mx-auto">
            Discover the rich culture, natural beauty, and exciting activities that make the Kawarthas a world-class destination.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {attractions.map((attraction, index) => (
              <motion.div
                key={attraction.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={getImageUrl(attraction.image)} 
                    alt={attraction.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#1B4D3E] text-white text-xs font-medium rounded-full">
                      {attraction.category}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="font-serif text-xl text-[#1C1C1C] mb-2 group-hover:text-[#1B4D3E] transition-colors">
                    {attraction.name}
                  </h3>
                  <p className="text-[#66605B] text-sm mb-3 line-clamp-3">
                    {attraction.description}
                  </p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2 text-[#66605B]">
                      <MapPin className="w-4 h-4 mt-0.5 text-[#1B4D3E] flex-shrink-0" />
                      <span className="line-clamp-2">{attraction.location}</span>
                    </div>
                    {attraction.phone && (
                      <div className="flex items-center gap-2 text-[#66605B]">
                        <Phone className="w-4 h-4 text-[#1B4D3E] flex-shrink-0" />
                        <a href={`tel:${attraction.phone}`} className="hover:text-[#1B4D3E]">
                          {attraction.phone}
                        </a>
                      </div>
                    )}
                  </div>

                  {attraction.website && (
                    <a
                      href={attraction.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 text-[#1B4D3E] font-medium text-sm hover:text-[#D4AF37] transition-colors"
                    >
                      Visit Website
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Golf Section */}
      <section className="py-16 bg-[#F0EBE5]">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-14 h-14 bg-[#1B4D3E] rounded-full flex items-center justify-center">
              <Mountain className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="font-serif text-3xl text-[#1C1C1C]">Golfing</h2>
              <p className="text-[#66605B]">With over 134 lakes, the topography presents scenic and challenging courses</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {golfCourses.map((course, idx) => (
              <motion.div
                key={course.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                <img src={getImageUrl(course.image)} alt={course.name} className="w-full h-40 object-cover" />
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-serif text-lg text-[#1C1C1C]">{course.name}</h3>
                    <span className="text-xs bg-[#1B4D3E] text-white px-2 py-0.5 rounded">{course.holes} Holes</span>
                  </div>
                  <p className="text-[#66605B] text-sm mb-3">{course.description}</p>
                  <p className="text-sm text-[#66605B] flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-[#1B4D3E] mt-0.5 flex-shrink-0" />
                    {course.address}
                  </p>
                  <p className="text-sm text-[#66605B] flex items-center gap-2 mt-1">
                    <Phone className="w-4 h-4 text-[#1B4D3E]" />
                    {course.phone}
                  </p>
                  {course.website && (
                    <a href={course.website} target="_blank" rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-sm text-[#1B4D3E] hover:text-[#D4AF37]">
                      Visit Website <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Winter Activities */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center">
              <Snowflake className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="font-serif text-3xl text-[#1C1C1C]">Winter Activities</h2>
              <p className="text-[#66605B]">Ontario's Snowmobile Paradise & More</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {winterActivities.map((activity, idx) => (
              <motion.div
                key={activity.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                <img src={getImageUrl(activity.image)} alt={activity.name} className="w-full h-48 object-cover" />
                <div className="p-5">
                  <h3 className="font-serif text-lg text-[#1C1C1C] mb-2">{activity.name}</h3>
                  <p className="text-[#66605B] text-sm mb-3">{activity.description}</p>
                  {activity.contact && (
                    <p className="text-sm text-[#66605B]">
                      <Phone className="w-4 h-4 inline mr-1" />
                      {activity.contact.phone}
                    </p>
                  )}
                  {activity.links && activity.links.map((link, i) => (
                    <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-sm text-[#1B4D3E] hover:text-[#D4AF37]">
                      {link.name} <ExternalLink className="w-3 h-3" />
                    </a>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Boating Info */}
      <section className="py-12 bg-blue-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center gap-4 mb-4">
            <Anchor className="w-8 h-8 text-blue-600" />
            <h2 className="font-serif text-2xl text-[#1C1C1C]">Boating in the Kawarthas</h2>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <p className="text-[#66605B] mb-3">
              <strong>Operator Requirements:</strong> All operators of craft under 4m (including personal watercraft) 
              must carry proof of competency - either a boating safety course certificate, pleasure craft operator card, 
              or completed rental-boat safety checklist.
            </p>
            <p className="text-sm text-[#66605B]">
              The operator card is good-for-life (75% pass mark required). Cards issued to non-residents by their state/country are accepted.
            </p>
          </div>
        </div>
      </section>

      {/* Useful Links */}
      <section className="py-16 bg-[#1B4D3E]">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="font-serif text-3xl text-white mb-8 text-center">Useful Links & Resources</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {usefulLinks.map((link, idx) => (
              <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer"
                className="bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-colors group">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-medium group-hover:text-[#D4AF37]">{link.name}</h3>
                    {link.description && <p className="text-white/60 text-sm">{link.description}</p>}
                  </div>
                  <ExternalLink className="w-5 h-5 text-white/60 group-hover:text-[#D4AF37]" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F0EBE5]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl text-[#1C1C1C] mb-4">Ready to Explore the Kawarthas?</h2>
          <p className="text-[#66605B] mb-8 max-w-2xl mx-auto">
            Book your stay at Big Bear Estate and experience the best of Ontario cottage country.
          </p>
          <a href="/booking" className="inline-block bg-[#1B4D3E] text-white px-8 py-4 rounded font-semibold hover:bg-[#153E32] transition-colors">
            Check Availability
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
